/**
 * useResults
 *
 * Hooks de cache e mutation para EventResultsPage e lançamentos de resultados.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sessionsApi, resultsApi, ResultItem } from '../lib/api';
import { TTL } from '../lib/queryClient';
import toast from 'react-hot-toast';

export const RESULTS_KEY = ['results'] as const;
export const SESSIONS_KEY = ['sessions'] as const;

/** Lista de splits de uma sessão */
export function useSessionSplits(sessionId: string) {
  return useQuery({
    queryKey: [...SESSIONS_KEY, sessionId, 'splits'],
    queryFn: () => sessionsApi.getSplits(sessionId),
    staleTime: TTL.MODERATE,
    gcTime: TTL.SLOW,
    enabled: !!sessionId,
  });
}

/**
 * Entradas de uma sessão (pilotos inscritos).
 *
 * confirmedOnly (eventos 4FUN): o backend já sabe filtrar pela confirmação de
 * presença da corrida (GET /sessions/{id}/entries?confirmed_only=true) — usado
 * como defesa em profundidade além do filtro local da tela.
 */
export function useSessionEntries(sessionId: string, confirmedOnly = false) {
  return useQuery({
    queryKey: [...SESSIONS_KEY, sessionId, 'entries', { confirmedOnly }],
    queryFn: () => sessionsApi.entries(sessionId, confirmedOnly),
    staleTime: TTL.FAST,
    gcTime: TTL.MODERATE,
    enabled: !!sessionId,
  });
}

/** Resultados publicados de uma sessão */
export function useSessionResults(sessionId: string) {
  return useQuery({
    queryKey: [...RESULTS_KEY, sessionId],
    queryFn: () => resultsApi.get(sessionId),
    staleTime: TTL.FAST,
    gcTime: TTL.MODERATE,
    enabled: !!sessionId,
  });
}

/** Mutation para preview de resultados (não irreversível) */
export function usePreviewResults() {
  return useMutation({
    mutationFn: ({ sessionId, results, splitId }: { sessionId: string; results: ResultItem[]; splitId?: string }) =>
      resultsApi.preview(sessionId, results, splitId),
    onError: () => {
      toast.error('Erro ao processar preview.');
    },
  });
}

/** Rascunho de lançamento parcial (RIVAIS) — consulta isolada por sessão */
export function useDraftResults(sessionId: string) {
  return useQuery({
    queryKey: [...RESULTS_KEY, sessionId, 'draft'],
    queryFn: () => resultsApi.getDraft(sessionId),
    staleTime: TTL.FAST,
    gcTime: TTL.MODERATE,
    enabled: !!sessionId,
  });
}

/** Mutation para salvar rascunho parcial (sem eRS/mRCI) */
export function useSaveDraftResults() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, results }: { sessionId: string; results: ResultItem[] }) =>
      resultsApi.saveDraft(sessionId, results),
    onSuccess: (_, { sessionId }) => {
      toast.success('Rascunho salvo.');
      qc.invalidateQueries({ queryKey: [...RESULTS_KEY, sessionId, 'draft'] });
    },
    onError: (error: any) => {
      const msg = error?.detail || error?.response?.data?.detail || error?.message || 'Erro ao salvar rascunho.';
      toast.error(msg);
    },
  });
}

/** Mutation para publicar resultados definitivamente */
export function usePublishResults() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, results, splitId }: { sessionId: string; results: ResultItem[], splitId?: string }) =>
      resultsApi.publish(sessionId, results, splitId),
    onSuccess: (_, { sessionId }) => {
      toast.success('Resultados processados e lançados com sucesso!');
      // Invalida cache de resultados e de sessões para atualizar UI
      qc.invalidateQueries({ queryKey: [...RESULTS_KEY, sessionId] });
      qc.invalidateQueries({ queryKey: [...SESSIONS_KEY, sessionId] });
      qc.invalidateQueries({ queryKey: ['events'] }); // Pode atualizar standings
    },
    onError: (error: any) => {
      const msg = error?.detail || error?.response?.data?.detail || error?.message || 'Erro fatal ao consolidar resultados.';
      toast.error(msg);
    },
  });
}
